/**
 * Analytics Provider
 * Wraps the app to provide automatic analytics tracking
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { analyticsService } from '@/services/analytics.service';
import { useAuthStore } from '@/stores/authStore';

interface AnalyticsProviderProps {
  children: React.ReactNode;
}

/**
 * Get page category from pathname
 */
function getPageCategory(pathname: string): string {
  if (pathname.startsWith('/deals')) return 'deals';
  if (pathname.startsWith('/properties')) return 'properties';
  if (pathname.startsWith('/contacts')) return 'contacts';
  if (pathname.startsWith('/funds')) return 'funds';
  if (pathname.startsWith('/tasks')) return 'tasks';
  if (pathname.startsWith('/communications')) return 'communications';
  if (pathname.startsWith('/documents')) return 'documents';
  if (pathname.startsWith('/reports')) return 'reports';
  if (pathname.startsWith('/research')) return 'research';
  if (pathname.startsWith('/settings')) return 'settings';
  if (pathname === '/' || pathname === '/dashboard') return 'dashboard';
  if (pathname === '/login') return 'auth';
  return 'other';
}

export function AnalyticsProvider({ children }: AnalyticsProviderProps) {
  const location = useLocation();
  // The id, not the user object: supabase-js re-announces the session on every
  // tab refocus and authStore stores a new user object each time.
  const userId = useAuthStore((s) => s.user?.id);
  const initialized = useAuthStore((s) => s.initialized);
  const previousPath = useRef<string | null>(null);
  const sessionInitialized = useRef(false);
  
  // Initialize or restore session when user auth state changes
  useEffect(() => {
    if (!initialized) return;
    
    if (userId && !sessionInitialized.current) {
      // User is logged in but no session exists - restore/create one
      const existingSession = analyticsService.getCurrentSession();
      
      if (!existingSession) {
        // Start a new session (this happens when user refreshes page while logged in)
        analyticsService.startSession(userId, 'email', 'session_restore').catch(err => {
          console.warn('Failed to restore analytics session:', err);
        });
      }
      
      sessionInitialized.current = true;
    } else if (!userId && sessionInitialized.current) {
      // User logged out
      sessionInitialized.current = false;
    }
  }, [userId, initialized]);
  
  // Track page views on route change
  useEffect(() => {
    // Don't track if not logged in or on auth pages
    if (!userId) return;
    
    const category = getPageCategory(location.pathname);
    
    // Skip tracking for auth pages
    if (category === 'auth') return;
    
    analyticsService.startPageView({
      pagePath: location.pathname,
      pageTitle: document.title,
      pageCategory: category,
      referrerPath: previousPath.current || undefined,
      queryParams: Object.fromEntries(new URLSearchParams(location.search)),
    });
    
    previousPath.current = location.pathname;
  }, [location.pathname, location.search, userId]);
  
  // Set up global error tracking
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      analyticsService.trackError({
        errorType: 'javascript',
        errorMessage: event.message,
        errorStack: event.error?.stack,
        pagePath: window.location.pathname,
      });
    };
    
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const error = event.reason;
      analyticsService.trackError({
        errorType: 'javascript',
        errorMessage: error?.message || 'Unhandled Promise Rejection',
        errorStack: error?.stack,
        pagePath: window.location.pathname,
      });
    };
    
    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);
  
  return <>{children}</>;
}

export default AnalyticsProvider;
